import { Component, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
    MAT_DIALOG_DATA,
    MatDialogModule,
    MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { OrganisationService } from 'app/core/organisation/organisation.service';
import { Observable, take } from 'rxjs';

export interface TwoFactorPromptData {
    /** The user has 2FA on and only needs to enter a code; otherwise they need to set it up. */
    enrolled: boolean;
    verify: (code: string) => Observable<unknown>;
}

/**
 * Asked when the organization requires 2FA for Manager and above and this sign-in has no code
 * yet. Closes with `true` once a code was accepted, so the interceptor can retry the request.
 */
@Component({
    selector: 'two-factor-prompt-dialog',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatDialogModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        TranslocoModule,
    ],
    template: `
        <ng-container *transloco="let t; read: 'settings.two-factor.prompt'">
            <h2 mat-dialog-title>{{ t('title') }}</h2>
            <mat-dialog-content>
                @if (data.enrolled) {
                    <p class="text-secondary mb-4">{{ t('enter-code') }}</p>
                    <mat-form-field class="w-full" subscriptSizing="dynamic">
                        <mat-label>{{ t('code-label') }}</mat-label>
                        <input
                            matInput
                            [formControl]="code"
                            autocomplete="one-time-code"
                            inputmode="text"
                            (keydown.enter)="submit()"
                            cdkFocusInitial
                        />
                    </mat-form-field>
                    <p class="text-secondary mt-2 text-sm">
                        {{ t('recovery-hint') }}
                    </p>
                    @if (error) {
                        <p class="mt-2 text-sm text-warn">{{ error }}</p>
                    }
                } @else {
                    <p class="text-secondary">{{ t('not-enrolled') }}</p>
                }
            </mat-dialog-content>
            <mat-dialog-actions align="end">
                <button mat-button [mat-dialog-close]="false">
                    {{ t('cancel') }}
                </button>
                @if (data.enrolled) {
                    <button
                        mat-flat-button
                        color="primary"
                        [disabled]="busy || code.invalid"
                        (click)="submit()"
                    >
                        {{ t('confirm') }}
                    </button>
                } @else {
                    <button mat-flat-button color="primary" (click)="setUp()">
                        {{ t('set-up') }}
                    </button>
                }
            </mat-dialog-actions>
        </ng-container>
    `,
})
export class TwoFactorPromptDialogComponent {
    data = inject<TwoFactorPromptData>(MAT_DIALOG_DATA);
    private _ref = inject(
        MatDialogRef<TwoFactorPromptDialogComponent, boolean>
    );
    private _router = inject(Router);
    private _organisation = inject(OrganisationService);
    private _transloco = inject(TranslocoService);

    code = new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.minLength(6)],
    });
    busy = false;
    error = '';

    submit(): void {
        if (this.busy || this.code.invalid) return;
        this.busy = true;
        this.error = '';
        this.data.verify(this.code.value.trim()).subscribe({
            next: () => this._ref.close(true),
            error: (err) => {
                this.busy = false;
                this.error =
                    err?.error?.message ??
                    this._transloco.translate('settings.two-factor.wrong-code');
            },
        });
    }

    setUp(): void {
        this._ref.close(false);
        this._organisation.organisation$.pipe(take(1)).subscribe((org) => {
            if (!org) return;
            void this._router.navigate([`/${(org as any).slug}/organization`], {
                queryParams: { panel: 'security' },
            });
        });
    }
}
